import React, { useEffect, useMemo, useState } from 'react';
import { Button, Container, Form, Table } from 'react-bootstrap';
import {
  FaChevronDown,
  FaChevronUp,
  FaFileExcel,
  FaPencilAlt,
  FaPercentage,
  FaSearch,
} from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import AuthStorage from '~/Utils/AuthStorage';
import { exportarEmExcel } from '~/Utils/Helpers';
import {
  listarFechamentosPj,
  listarPrestadoresPj,
} from '~/Services/Service';
import { CARACTERISTICA_EDITA_EXCLUI_FECHAMENTO_PJ } from '~/constant';
import {
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
  descreverTipoRemuneracao,
  formatarRemuneracao,
} from '../Prestadores/utils/formatters';
import ModalAddIPCA from './components/ModalAddIPCA';
import ModalErrosImportacao from './components/ModalErrosImportacao';
import ModalImportarPlanilha from './components/ModalImportarPlanilha';
import ModalNovoFechamento from './components/ModalNovoFechamento';
import GridPagination from '../components/GridPagination';
import SortableHeader, {
  alternarOrdenacao,
  ordenarRegistros,
} from '../components/SortableHeader';
import '../styles.css';

const PAGE_SIZE = 10;

const moeda = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarCompetencia = value => {
  if (!value) return '-';
  const partes = String(value).split('-');
  if (partes.length < 2) return value;
  return `${partes[1]}/${partes[0]}`;
};

const classeBadgeModalidade = tipo => {
  if (tipo === TIPO_REMUNERACAO_HORA) return 'hpj-badge hpj-badge--blue';
  if (tipo === TIPO_REMUNERACAO_FIXO) return 'hpj-badge hpj-badge--green';
  return 'hpj-badge hpj-badge--slate';
};

const decimalParaHoras = value => {
  if (value === null || value === undefined || value === '') return '-';
  const numero = Number(value);
  if (Number.isNaN(numero)) return '-';
  const totalMinutos = Math.round(numero * 60);
  const horas = Math.trunc(totalMinutos / 60);
  const minutos = Math.abs(totalMinutos % 60);
  return `${horas}h ${String(minutos).padStart(2, '0')}min`;
};

const decimalExcel = valor => {
  if (valor === null || valor === undefined || valor === '') return '';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return '';
  return numero.toFixed(2).replace('.', ',');
};

const textoExcel = valor => (valor === null || valor === undefined ? '' : String(valor));

const colunasOrdenacao = {
  competencia: item => item.competencia || '',
  prestador: item => item.razao_social || '',
  modalidade: item => descreverTipoRemuneracao(item.tipo_remuneracao_snapshot),
  horas: item => Number(item.horas_trabalhadas || 0),
  remuneracao: item => Number(item.valor_hora || item.valor_fixo_snapshot || 0),
  valorNf: item => Number(item.valor_nf || 0),
  valor13: item => Number(item.valor13 || 0),
  valor14: item => Number(item.valor14 || 0),
  valorFundo: item => Number(item.valor_fundo || 0),
  total: item => Number(item.total30 || 0),
  nf: item => item.num_nf || '',
};

const Fechamento = () => {
  const [fechamentos, setFechamentos] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [prestadorFiltro, setPrestadorFiltro] = useState(null);
  const [competenciaFiltro, setCompetenciaFiltro] = useState('');
  const [pesquisou, setPesquisou] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [fechamentoEdicao, setFechamentoEdicao] = useState(null);
  const [modalImportar, setModalImportar] = useState(false);
  const [modalIpca, setModalIpca] = useState(false);
  const [errosImportacao, setErrosImportacao] = useState(null);
  const [fechamentosExpandidos, setFechamentosExpandidos] = useState({});
  const [page, setPage] = useState(1);
  const [ordenacao, setOrdenacao] = useState(null);

  const podeEditarFechamento = AuthStorage.temCaracteristica(
    CARACTERISTICA_EDITA_EXCLUI_FECHAMENTO_PJ
  );

  const montarFiltros = () => ({
    codigoPj: prestadorFiltro ? prestadorFiltro.value : null,
    competenciaInicio: competenciaFiltro || null,
    competenciaFim: competenciaFiltro || null,
  });

  const carregar = async filtrosCustomizados => {
    try {
      setLoading(true);
      const resultado = await listarFechamentosPj(
        filtrosCustomizados || montarFiltros()
      );
      setFechamentos(Array.isArray(resultado) ? resultado : []);
      setPesquisou(true);
      setPage(1);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar fechamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listarPrestadoresPj()
      .then(resultado => setPrestadores(Array.isArray(resultado) ? resultado : []))
      .catch(err => toast.error(err.message || 'Erro ao carregar prestadores.'));
  }, []);

  const prestadorOptions = useMemo(
    () =>
      prestadores
        .map(item => ({
          value: item.codigo,
          label: item.nome_fantasia || item.razao_social || `PJ ${item.codigo}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [prestadores]
  );

  const ordenarPor = key => {
    setOrdenacao(atual => alternarOrdenacao(atual, key));
    setPage(1);
  };

  const dadosExibidos = useMemo(
    () => ordenarRegistros(fechamentos, ordenacao, colunasOrdenacao),
    [fechamentos, ordenacao]
  );

  const fechamentosPaginados = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return dadosExibidos.slice(start, start + PAGE_SIZE);
  }, [dadosExibidos, page]);

  const colunasTabela = podeEditarFechamento ? 12 : 11;

  const obterMensagemVazia = () => {
    if (loading) return 'Carregando...';
    if (pesquisou) return 'Nenhum fechamento encontrado';
    return 'Informe os filtros e clique em Buscar.';
  };
  const mensagemVazia = obterMensagemVazia();

  const toggleFechamentoMobile = codigo => {
    setFechamentosExpandidos(prev => ({
      ...prev,
      [codigo]: !prev[codigo],
    }));
  };

  const abrirNovoFechamento = () => {
    setFechamentoEdicao(null);
    setModal(true);
  };

  const abrirEdicao = item => {
    setFechamentoEdicao(item);
    setModal(true);
  };

  const fecharModalFechamento = () => {
    setFechamentoEdicao(null);
    setModal(false);
  };

  const exportarExcel = () => {
    if (!dadosExibidos.length) {
      toast.info('Não há dados exibidos para exportar.');
      return;
    }

    const linhas = [
      [
        'cnpj',
        'competencia',
        'horas',
        'valor_hora',
        'nf',
        'valor_nf',
        'valor_13',
        'valor_14',
        'valor_fundo',
        'total',
      ],
      ...dadosExibidos.map(item => [
        textoExcel(item.cnpj),
        textoExcel(item.competencia),
        decimalExcel(item.horas_trabalhadas),
        decimalExcel(item.valor_hora),
        textoExcel(item.num_nf),
        decimalExcel(item.valor_nf),
        decimalExcel(item.valor13),
        decimalExcel(item.valor14),
        decimalExcel(item.valor_fundo),
        decimalExcel(item.total30),
      ]),
    ];

    exportarEmExcel(linhas, 'fechamento-pj.xlsx');
  };

  const recarregarAposAlteracao = async () => {
    await carregar();
    fecharModalFechamento();
  };

  const carregarTodosAposImportacao = async () => {
    setPrestadorFiltro(null);
    setCompetenciaFiltro('');
    await carregar({
      codigoPj: null,
      competenciaInicio: null,
      competenciaFim: null,
    });
  };

  return (
    <div className="hpj-page">
      <Container fluid className="p-0">
        <div className="hpj-shell hpj-shell--xxwide">
          <div className="hpj-panel">
            <div className="hpj-title-row hpj-title-row--fechamento">
              <div>
                <div className="hpj-eyebrow">Departamento Jurídico</div>
                <h1 className="hpj-title">Fechamento Mensal</h1>
              </div>
              <div className="hpj-title-actions">
                <Button
                  className="hpj-btn hpj-btn-outline"
                  variant="outline-secondary"
                  onClick={() => setModalIpca(true)}
                >
                  <FaPercentage className="mr-2" />
                  Parâmetros IPCA
                </Button>
                <Button
                  className="hpj-btn hpj-btn-outline"
                  variant="outline-secondary"
                  onClick={() => setModalImportar(true)}
                >
                  Importar Planilha
                </Button>
                <Button
                  className="hpj-btn hpj-btn-primary"
                  variant="primary"
                  onClick={abrirNovoFechamento}
                >
                  + Novo Fechamento
                </Button>
              </div>
            </div>

            <div className="hpj-table-controls-row">
              <div className="hpj-table-meta-row">
                <span>{dadosExibidos.length} registro(s)</span>
              </div>

              <div className="hpj-filter-row hpj-filter-row--fechamento">
                <Form.Group className="hpj-filter-field hpj-filter-field--pj">
                  <Form.Label>Prestador PJ</Form.Label>
                  <Select
                    classNamePrefix="hpj-user-select"
                    isClearable
                    isSearchable
                    options={prestadorOptions}
                    placeholder="Todos"
                    value={prestadorFiltro}
                    onChange={setPrestadorFiltro}
                  />
                </Form.Group>
                <Form.Group className="hpj-filter-field">
                  <Form.Label>Competência</Form.Label>
                  <Form.Control
                    className="hpj-filter-control"
                    type="month"
                    value={competenciaFiltro}
                    onChange={event => setCompetenciaFiltro(event.target.value)}
                  />
                </Form.Group>
                <div className="hpj-filter-actions">
                  <Button
                    className="hpj-btn hpj-btn-primary"
                    variant="primary"
                    onClick={() => carregar()}
                    disabled={loading}
                  >
                    <FaSearch className="mr-2" />
                    Buscar
                  </Button>
                </div>
              </div>
            </div>

            <div className="hpj-card hpj-card--overflow hpj-card--responsive-list">
              <div className="hpj-table-scroll">
                <Table className="hpj-table" responsive>
                  <thead>
                    <tr>
                      <SortableHeader sortKey="competencia" ordenacao={ordenacao} onSort={ordenarPor}>
                        Competência
                      </SortableHeader>
                      <SortableHeader sortKey="prestador" ordenacao={ordenacao} onSort={ordenarPor}>
                        Prestador
                      </SortableHeader>
                      <SortableHeader sortKey="modalidade" ordenacao={ordenacao} onSort={ordenarPor}>
                        Modalidade
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="horas" ordenacao={ordenacao} onSort={ordenarPor}>
                        Horas
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="remuneracao" ordenacao={ordenacao} onSort={ordenarPor}>
                        Remuneração
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="valorNf" ordenacao={ordenacao} onSort={ordenarPor}>
                        Valor NF
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="valor13" ordenacao={ordenacao} onSort={ordenarPor}>
                        13ª
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="valor14" ordenacao={ordenacao} onSort={ordenarPor}>
                        14ª
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="valorFundo" ordenacao={ordenacao} onSort={ordenarPor}>
                        Fundo
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="total" ordenacao={ordenacao} onSort={ordenarPor}>
                        Total
                      </SortableHeader>
                      <SortableHeader className="hpj-col-num" sortKey="nf" ordenacao={ordenacao} onSort={ordenarPor}>
                        Nº NF
                      </SortableHeader>
                      {podeEditarFechamento && <th>Ação</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {fechamentosPaginados.map(item => (
                      <tr key={item.codigo}>
                        <td className="hpj-main-text">
                          {formatarCompetencia(item.competencia)}
                        </td>
                        <td>{item.razao_social || '-'}</td>
                        <td>
                          <span className={classeBadgeModalidade(item.tipo_remuneracao_snapshot)}>
                            {descreverTipoRemuneracao(item.tipo_remuneracao_snapshot)}
                          </span>
                        </td>
                        <td className="hpj-col-num">{decimalParaHoras(item.horas_trabalhadas)}</td>
                        <td className="hpj-col-num">
                          {formatarRemuneracao(
                            item.tipo_remuneracao_snapshot,
                            item.valor_hora,
                            item.valor_fixo_snapshot,
                            item.horas_base_snapshot,
                          )}
                        </td>
                        <td className="hpj-main-text hpj-col-num">
                          {moeda(item.valor_nf)}
                        </td>
                        <td className="hpj-col-num">{moeda(item.valor13)}</td>
                        <td className="hpj-col-num">{moeda(item.valor14)}</td>
                        <td className="hpj-col-num">{moeda(item.valor_fundo)}</td>
                        <td className="hpj-main-text hpj-col-num">{moeda(item.total30)}</td>
                        <td className="hpj-col-num">{item.num_nf || '-'}</td>
                        {podeEditarFechamento && (
                          <td>
                            <Button
                              className="hpj-icon-btn"
                              variant="link"
                              title="Editar fechamento"
                              aria-label="Editar fechamento"
                              onClick={() => abrirEdicao(item)}
                            >
                              <FaPencilAlt />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {!dadosExibidos.length && (
                      <tr>
                        <td colSpan={colunasTabela} className="text-center text-muted py-4">
                          {mensagemVazia}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="hpj-mobile-list">
                {fechamentosPaginados.map(item => {
                  const expandido = !!fechamentosExpandidos[item.codigo];
                  return (
                    <div className="hpj-mobile-card hpj-mobile-card--compact" key={item.codigo}>
                      <div className="hpj-mobile-card-head hpj-mobile-card-head--compact">
                        <div className="hpj-mobile-title-block">
                          <div className="hpj-mobile-title">{item.razao_social || '-'}</div>
                          <div className="hpj-mobile-subtitle">
                            Competência {formatarCompetencia(item.competencia)}
                          </div>
                          <div className="hpj-mobile-kpi hpj-mobile-kpi--inline">
                            {moeda(item.total30)}
                          </div>
                        </div>
                        <Button
                          className="hpj-mobile-expand-btn"
                          size="sm"
                          variant="link"
                          aria-expanded={expandido}
                          aria-label={expandido ? 'Recolher fechamento' : 'Expandir fechamento'}
                          onClick={() => toggleFechamentoMobile(item.codigo)}
                        >
                          {expandido ? <FaChevronUp /> : <FaChevronDown />}
                        </Button>
                      </div>
                      {expandido && (
                        <>
                          <div className="hpj-mobile-grid hpj-mobile-grid--three hpj-mobile-grid--expanded">
                            <div className="hpj-mobile-field">
                              <span>Modalidade</span>
                              <strong>
                                <span className={classeBadgeModalidade(item.tipo_remuneracao_snapshot)}>
                                  {descreverTipoRemuneracao(item.tipo_remuneracao_snapshot)}
                                </span>
                              </strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>Horas</span>
                              <strong>{decimalParaHoras(item.horas_trabalhadas)}</strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>Remuneração</span>
                              <strong>
                                {formatarRemuneracao(
                                  item.tipo_remuneracao_snapshot,
                                  item.valor_hora,
                                  item.valor_fixo_snapshot,
                                  item.horas_base_snapshot,
                                )}
                              </strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>Valor NF</span>
                              <strong>{moeda(item.valor_nf)}</strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>13ª</span>
                              <strong>{moeda(item.valor13)}</strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>14ª</span>
                              <strong>{moeda(item.valor14)}</strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>Fundo</span>
                              <strong>{moeda(item.valor_fundo)}</strong>
                            </div>
                            <div className="hpj-mobile-field">
                              <span>Nº NF</span>
                              <strong>{item.num_nf || '-'}</strong>
                            </div>
                          </div>
                          {podeEditarFechamento && (
                            <div className="hpj-mobile-actions">
                              <Button
                                className="hpj-mobile-action-btn"
                                variant="outline-secondary"
                                onClick={() => abrirEdicao(item)}
                              >
                                <FaPencilAlt className="mr-2" />
                                Editar
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {!dadosExibidos.length && (
                  <div className="hpj-mobile-empty">
                    {mensagemVazia}
                  </div>
                )}
              </div>
              <GridPagination
                page={page}
                pageSize={PAGE_SIZE}
                totalItems={dadosExibidos.length}
                onChange={setPage}
              />
            </div>
            <div className="hpj-table-footer-actions">
              <Button
                className="hpj-btn hpj-btn-outline"
                variant="outline-secondary"
                onClick={exportarExcel}
                disabled={!dadosExibidos.length}
              >
                <FaFileExcel className="mr-2" />
                Exportar Excel
              </Button>
            </div>
          </div>
        </div>
      </Container>

      <ModalNovoFechamento
        show={modal}
        fechamento={fechamentoEdicao}
        podeExcluir={podeEditarFechamento}
        onHide={fecharModalFechamento}
        onSalvo={recarregarAposAlteracao}
        onExcluido={recarregarAposAlteracao}
      />
      <ModalImportarPlanilha
        show={modalImportar}
        onHide={() => setModalImportar(false)}
        onImportado={carregarTodosAposImportacao}
        onErroImportacao={erros => {
          setErrosImportacao(erros);
          setModalImportar(false);
        }}
      />
      <ModalErrosImportacao
        show={!!errosImportacao}
        erros={errosImportacao}
        onHide={() => setErrosImportacao(null)}
      />
      <ModalAddIPCA show={modalIpca} onHide={() => setModalIpca(false)} />
    </div>
  );
};

export default Fechamento;
